import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Search, ExternalLink, ShieldCheck, ShieldOff } from "lucide-react";

/** Business customers grid — claim queue, owner accounts, verify/suspend. */
export default function CmsBusinesses() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [state, setState] = useState("");
  const [country, setCountry] = useState("US");
  const [loading, setLoading] = useState(false);
  const [claimQueue, setClaimQueue] = useState({ total: 0 });

  const load = async () => {
    setLoading(true);
    try {
      const r = await http.get("/food-businesses", { params: { q: q || undefined, state: state || undefined, country, limit: 40 } });
      setItems(r.data?.items || []);
      setTotal(r.data?.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load businesses");
    } finally {
      setLoading(false);
    }
  };
  const loadClaims = async () => {
    try {
      // Reuse the existing pending-products list as a proxy for claim signals;
      // the /admin/foodpulse/business-suggestions surface has the real queue.
      const r = await http.get("/admin/foodpulse/business-suggestions", { params: { limit: 1 } }).catch(() => null);
      setClaimQueue({ total: r?.data?.total || 0 });
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); loadClaims(); /* eslint-disable-next-line */ }, [country]);

  const toggleVerify = async (slug, verified) => {
    try {
      await http.patch(`/food-businesses/${encodeURIComponent(slug)}`, { verified });
      toast.success(verified ? "Business verified" : "Verification revoked");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  return (
    <div className="max-w-6xl" data-testid="cms-businesses">
      <Link to="/admin/cms" className="mb-3 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to CMS home
      </Link>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Business Customers</h1>
          <p className="mt-1 text-sm text-slate-500">Search, verify and moderate — {total.toLocaleString()} businesses in the directory.</p>
        </div>
        {claimQueue.total > 0 && (
          <Link to="/admin/foodpulse/business-suggestions" data-testid="cms-businesses-claim-queue" className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 hover:bg-amber-100">
            {claimQueue.total} claim{claimQueue.total === 1 ? "" : "s"} awaiting review →
          </Link>
        )}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="cms-businesses-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search name, city, state…"
            className="pl-8"
          />
        </div>
        <select value={country} onChange={(e) => setCountry(e.target.value)} className="rounded-md border border-slate-200 px-2 py-1.5 text-sm">
          <option value="US">US</option>
          <option value="IN">India</option>
          <option value="">All countries</option>
        </select>
        <Input value={state} onChange={(e) => setState(e.target.value)} placeholder="State" className="w-24" />
        <Button variant="outline" onClick={load} disabled={loading}>{loading ? "Searching…" : "Search"}</Button>
      </div>

      <div className="mt-4 overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-slate-500">
            <tr>
              <th className="px-3 py-2 font-medium">Business</th>
              <th className="px-3 py-2 font-medium">Location</th>
              <th className="px-3 py-2 font-medium">Type</th>
              <th className="px-3 py-2 font-medium">Verified</th>
              <th className="px-3 py-2 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((b) => (
              <tr key={b.slug || b.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-3 py-2">
                  <div className="font-medium text-slate-900">{b.name}</div>
                  <div className="text-xs text-slate-500">{b.slug}</div>
                </td>
                <td className="px-3 py-2 text-slate-700">
                  {b.city ? `${b.city}, ${b.state || ""}` : (b.state || "—")}
                </td>
                <td className="px-3 py-2 text-slate-700">{b.business_type || b.type || "—"}</td>
                <td className="px-3 py-2">{b.verified ? "✓" : ""}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <Link
                    to={`/food-pulse/businesses/${b.slug}`}
                    target="_blank"
                    className="mr-1 inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-700 hover:border-emerald-300 hover:bg-emerald-50"
                  >
                    <ExternalLink className="h-3 w-3" /> View
                  </Link>
                  <button
                    onClick={() => toggleVerify(b.slug, !b.verified)}
                    data-testid={`cms-business-verify-${b.slug}`}
                    className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-700 hover:border-emerald-300 hover:bg-emerald-50"
                  >
                    {b.verified ? <ShieldOff className="h-3 w-3" /> : <ShieldCheck className="h-3 w-3" />} {b.verified ? "Unverify" : "Verify"}
                  </button>
                </td>
              </tr>
            ))}
            {items.length === 0 && !loading && (
              <tr><td colSpan={5} className="p-6 text-center text-slate-500">No businesses match.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
